import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Typography,
    Box,
    Alert,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Checkbox,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import Layout from "../../../layout/layout";
import axiosInstance from "../../../api/axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

const AppConfigManager = () => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [configs, setConfigs] = useState([]);
    const [filteredConfigs, setFilteredConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("success");
    const [searchQuery, setSearchQuery] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [formData, setFormData] = useState({
        value: "",
        description: ""
    });
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        key: "",
        value: "",
        description: ""
    });
    const [createValueType, setCreateValueType] = useState("string");
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);

    useEffect(() => {
        const password = prompt("Enter password to access App Config Manager:");
        if (password === "admin123") {
            setIsAuthenticated(true);
        } else {
            alert("Incorrect password. Access denied.");
            window.history.back();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchConfigs();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredConfigs(configs);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = configs.filter(
                (config) =>
                    config.key.toLowerCase().includes(query) ||
                    config.description.toLowerCase().includes(query)
            );
            setFilteredConfigs(filtered);
        }
    }, [searchQuery, configs]);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("config/");
            setConfigs(response.data);
            setFilteredConfigs(response.data);
        } catch (error) {
            setMessage("Failed to load configurations");
            setMessageType("error");
            console.error("Error fetching configs:", error);
        } finally {
            setLoading(false);
        }
    };

    // Detect data type of a value
    const detectDataType = (value) => {
        if (typeof value === "boolean") return "boolean";
        if (typeof value === "number") {
            return Number.isInteger(value) ? "integer" : "float";
        }
        if (typeof value === "string") {
            // Check if it's a date format
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) return "date";
            return "string";
        }
        if (typeof value === "object") return "object";
        return "unknown";
    };

    // Render appropriate input field based on data type
    const renderInputField = (config) => {
        const dataType = detectDataType(config.value);
        const commonProps = {
            fullWidth: true,
            variant: "outlined",
            margin: "normal"
        };

        switch (dataType) {
            case "boolean":
                return (
                    <Box sx={{ my: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Value (Boolean)
                        </Typography>
                        <Checkbox
                            checked={formData.value === true || formData.value === "true"}
                            onChange={(e) => setFormData({ ...formData, value: e.target.checked })}
                        />
                    </Box>
                );
            case "integer":
                return (
                    <TextField
                        {...commonProps}
                        label="Value (Integer)"
                        type="number"
                        inputProps={{ step: "1" }}
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) || 0 })}
                    />
                );
            case "float":
                return (
                    <TextField
                        {...commonProps}
                        label="Value (Number)"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                    />
                );
            case "date":
                return (
                    <TextField
                        {...commonProps}
                        label="Value (Date)"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    />
                );
            default:
                return (
                    <TextField
                        {...commonProps}
                        label="Value (Text)"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    />
                );
        }
    };

    const handleEditConfig = (config) => {
        setSelectedConfig(config);
        setFormData({
            value: config.value,
            description: config.description
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedConfig(null);
        setFormData({ value: "", description: "" });
    };

    const handleSaveConfig = async () => {
        if (!selectedConfig) return;

        try {
            setLoading(true);
            await axiosInstance.put("config/update/key/", {
                key: selectedConfig.key,
                value: formData.value,
                description: formData.description
            });

            setMessage(`"${selectedConfig.key}" updated successfully!`);
            setMessageType("success");
            handleCloseDialog();
            await fetchConfigs();
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage("Failed to update configuration");
            setMessageType("error");
            console.error("Error updating config:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateConfig = async () => {
        if (!createFormData.key || createFormData.value === "") {
            setMessage("Key and value are required");
            setMessageType("error");
            return;
        }

        try {
            setLoading(true);
            let value = createFormData.value;

            // Convert value based on type
            switch (createValueType) {
                case "boolean":
                    value = createFormData.value === "true" || createFormData.value === true;
                    break;
                case "integer":
                    value = parseInt(createFormData.value);
                    break;
                case "float":
                    value = parseFloat(createFormData.value);
                    break;
                default:
                    value = String(createFormData.value);
            }

            await axiosInstance.post("config/create/", {
                key: createFormData.key,
                value: value,
                description: createFormData.description
            });

            setMessage(`"${createFormData.key}" created successfully!`);
            setMessageType("success");
            setOpenCreateDialog(false);
            setCreateFormData({ key: "", value: "", description: "" });
            setCreateValueType("string");
            await fetchConfigs();
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage(error.response?.data?.error || "Failed to create configuration");
            setMessageType("error");
            console.error("Error creating config:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConfig = (config) => {
        setConfigToDelete(config);
        setDeleteConfirmDialog(true);
    };

    const confirmDeleteConfig = async () => {
        if (!configToDelete) return;

        try {
            setLoading(true);
            await axiosInstance.delete("config/delete/", {
                data: { id: configToDelete.id }
            });

            setMessage(`"${configToDelete.key}" deleted successfully!`);
            setMessageType("success");
            setDeleteConfirmDialog(false);
            setConfigToDelete(null);
            await fetchConfigs();
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage("Failed to delete configuration");
            setMessageType("error");
            console.error("Error deleting config:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatValue = (value) => {
        if (typeof value === "boolean") return value ? "True" : "False";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
    };

    if (!isAuthenticated) {
        return null;
    }

    const body = (
        <div>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    App Config Manager
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage application configurations and settings
                </Typography>
            </Box>

            {message && (
                <Alert severity={messageType} sx={{ mb: 3 }}>
                    {message}
                </Alert>
            )}

            {/* Create Button */}
            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateDialog(true)}
                >
                    Create New Config
                </Button>
            </Box>

            {/* Search Bar */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search by key or description..."
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            {/* Configs Table */}
            {loading && configs.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>Key</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Last Updated</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }} align="center">
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredConfigs.length > 0 ? (
                                filteredConfigs.map((config) => (
                                    <TableRow key={config.id}>
                                        <TableCell sx={{ fontFamily: "monospace" }}>
                                            {config.key}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", maxWidth: 300 }}>
                                            {formatValue(config.value)}
                                        </TableCell>
                                        <TableCell>{config.description}</TableCell>
                                        <TableCell>
                                            {new Date(config.updated_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEditConfig(config)}
                                                sx={{ mr: 1 }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteConfig(config)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">
                                            {searchQuery ? "No configurations found matching your search." : "No configurations available."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit Configuration: <code>{selectedConfig?.key}</code>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {selectedConfig && renderInputField(selectedConfig)}
                    <TextField
                        fullWidth
                        label="Description"
                        placeholder="Enter description"
                        variant="outlined"
                        margin="normal"
                        multiline
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSaveConfig}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Config Dialog */}
            <Dialog open={openCreateDialog} onClose={() => !loading && setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Configuration</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Key (Unique Identifier)"
                        placeholder="e.g., app_name, max_users"
                        variant="outlined"
                        margin="normal"
                        value={createFormData.key}
                        onChange={(e) => setCreateFormData({ ...createFormData, key: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Value Type</InputLabel>
                        <Select
                            value={createValueType}
                            onChange={(e) => setCreateValueType(e.target.value)}
                            label="Value Type"
                        >
                            <MenuItem value="string">Text</MenuItem>
                            <MenuItem value="integer">Integer</MenuItem>
                            <MenuItem value="float">Float</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                        </Select>
                    </FormControl>
                    
                    {createValueType === "boolean" ? (
                        <Box sx={{ my: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Value
                            </Typography>
                            <Checkbox
                                checked={createFormData.value === "true" || createFormData.value === true}
                                onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.checked })}
                            />
                        </Box>
                    ) : createValueType === "date" ? (
                        <TextField
                            fullWidth
                            label="Value (Date)"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            margin="normal"
                            value={createFormData.value}
                            onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.value })}
                        />
                    ) : createValueType === "integer" ? (
                        <TextField
                            fullWidth
                            label="Value (Integer)"
                            type="number"
                            inputProps={{ step: "1" }}
                            variant="outlined"
                            margin="normal"
                            value={createFormData.value}
                            onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.value })}
                        />
                    ) : createValueType === "float" ? (
                        <TextField
                            fullWidth
                            label="Value (Float)"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            variant="outlined"
                            margin="normal"
                            value={createFormData.value}
                            onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.value })}
                        />
                    ) : (
                        <TextField
                            fullWidth
                            label="Value (Text)"
                            variant="outlined"
                            margin="normal"
                            multiline
                            rows={3}
                            value={createFormData.value}
                            onChange={(e) => setCreateFormData({ ...createFormData, value: e.target.value })}
                        />
                    )}
                    
                    <TextField
                        fullWidth
                        label="Description"
                        placeholder="Enter description"
                        variant="outlined"
                        margin="normal"
                        multiline
                        rows={2}
                        value={createFormData.description}
                        onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={handleCreateConfig}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? "Creating..." : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmDialog} onClose={() => !loading && setDeleteConfirmDialog(false)}>
                <DialogTitle>Delete Configuration?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{configToDelete?.key}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmDialog(false)} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={confirmDeleteConfig}
                        variant="contained"
                        color="error"
                        disabled={loading}
                    >
                        {loading ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );

    return <Layout title="App Config Manager" body={body} />;
};

export default AppConfigManager;
